/**
 * The client part reference lists, but only when they belong to the team being
 * planned.
 *
 * The lists are fetched one team at a time and kept in the saved state. Nothing
 * in that cache recorded which team it was fetched for, so any refresh that
 * quietly did not happen — the server offline at the moment the team was
 * switched, a half-finished fetch, a restored session — left one team being
 * planned against another team's allowlist. Client stock belonging to the wrong
 * team then flowed into both the Power BI merge and the call list, and neither
 * could tell that it had.
 *
 * With no matching tag, no list is handed back. Excluding client stock is the
 * safe direction: it can only leave parts out, never admit a part the team is
 * not allowed to fix, and the caller says plainly that the lists need
 * reloading.
 */
export function partReferencesForTeam({
  clientPartIds,
  clientPartIdsTeam,
  team,
} = {}) {
  const want = String(team || "").trim().toUpperCase();
  const have = String(clientPartIdsTeam || "").trim().toUpperCase();
  if (!want || !have || want !== have) return { refs: {}, stale: true };
  return { refs: clientPartIds || {}, stale: false };
}

// gradingLookup keys must be uppercase (matches the uppercased partId lookup below)
export function mergePartsByClient(pbiRows, clients, clientPartIds, gradingLookup, selectedTeam) {
  const merged = [];
  for (const row of pbiRows) {
    if (row.bad <= 0) continue;
    if (!row.partId) continue;
    const key = row.partId.toUpperCase();
    const matchedClient = clients.find((c) => key.startsWith(c.prefix.toUpperCase()));

    if (matchedClient) {
      const allowed = clientPartIds[matchedClient.id];
      // Client-prefixed stock is mixed across teams, so the team's part
      // reference list is the one and only gate on it. A team ticked onto a
      // part in Parts Grading is written straight into that list by
      // POST /api/grading/override-with-teams, so a hand-made grant arrives
      // here exactly the way an uploaded row does. Allocation must never grow
      // a second, private rule: the call-list parser reads the same reference
      // list, and the two have to answer the same question the same way.
      if (!allowed || !allowed.has(key)) continue;
      const gradeInfo = gradingLookup[key];
      if (!gradeInfo) continue;
      for (let i = 0; i < row.bad; i++) {
        merged.push({
          partId: row.partId,
          grade: gradeInfo.grade,
          team: gradeInfo.team || matchedClient.id.toUpperCase(),
          customer: row.customer,
          repReq: i < row.repReq ? 1 : 0,
          serial: `${matchedClient.id}-${row.partId}-${i + 1}`,
          source: matchedClient.id,
        });
      }
    } else {
      const gradeInfo = gradingLookup[key];
      if (!gradeInfo) continue;
      for (let i = 0; i < row.bad; i++) {
        merged.push({
          partId: row.partId,
          grade: gradeInfo.grade,
          team: gradeInfo.team,
          customer: row.customer,
          repReq: i < row.repReq ? 1 : 0,
          serial: `${row.partId}-${i + 1}`,
          source: 'team',
        });
      }
    }
  }
  return merged;
}

export function weightedInterleave(teamArr, clientArr, clientWeight) {
  if (clientWeight <= 0 || clientArr.length === 0) return [...teamArr];
  if (clientWeight >= 1 || teamArr.length === 0) return [...clientArr];
  const result = [];
  let ti = 0, ci = 0, acc = 0;
  while (ti < teamArr.length || ci < clientArr.length) {
    acc += clientWeight;
    if (acc >= 1 && ci < clientArr.length) {
      result.push(clientArr[ci++]);
      acc -= 1;
    } else if (ti < teamArr.length) {
      result.push(teamArr[ti++]);
    } else {
      result.push(clientArr[ci++]);
    }
  }
  return result;
}
